import * as React from 'react'

import {
  ILinkedItem,
  ResourceLinkContext,
  ResourceLinkContextValue,
} from './ResourceLinkContext'

interface IProps {
  sourceId: string | null
  linkedItem: ILinkedItem | null
  availableTargets: ILinkedItem[]
  onLink: (
    sourceId: string,
    targetId: string,
  ) => Promise<{ success: boolean; error?: string | null }>
  onUnlink: (
    sourceId: string,
  ) => Promise<{ success: boolean; error?: string | null }>
  children: React.ReactNode
}

export const ResourceLinkRoot: React.FC<IProps> = ({
  sourceId,
  linkedItem,
  availableTargets,
  onLink,
  onUnlink,
  children,
}) => {
  const [isPickerOpen, setIsPickerOpen] = React.useState(false)
  const [loading, setLoading] = React.useState(false)
  const [error, setError] = React.useState<string | null>(null)

  const openPicker = React.useCallback(() => {
    setError(null)
    setIsPickerOpen(true)
  }, [])

  const closePicker = React.useCallback(() => {
    setIsPickerOpen(false)
  }, [])

  const linkTo = React.useCallback(
    async (targetId: string) => {
      if (!sourceId) return
      setIsPickerOpen(false)
      setLoading(true)
      setError(null)

      try {
        const result = await onLink(sourceId, targetId)

        if (!result.success) {
          setError(result.error ?? 'Failed to link')
        }
      } catch {
        setError('Failed to link')
      } finally {
        setLoading(false)
      }
    },
    [sourceId, onLink],
  )

  const unlinkCurrent = React.useCallback(async () => {
    if (!sourceId) return
    setLoading(true)
    setError(null)

    try {
      const result = await onUnlink(sourceId)

      if (!result.success) {
        setError(result.error ?? 'Failed to unlink')
      }
    } catch {
      setError('Failed to unlink')
    } finally {
      setLoading(false)
    }
  }, [sourceId, onUnlink])

  const value = React.useMemo<ResourceLinkContextValue>(
    () => ({
      linkedItem,
      availableTargets,
      isPickerOpen,
      openPicker,
      closePicker,
      linkTo,
      unlinkCurrent,
      loading,
      error,
    }),
    [
      linkedItem,
      availableTargets,
      isPickerOpen,
      openPicker,
      closePicker,
      linkTo,
      unlinkCurrent,
      loading,
      error,
    ],
  )

  return (
    <ResourceLinkContext.Provider value={value}>
      {children}
    </ResourceLinkContext.Provider>
  )
}
